import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";

function RegisterPage() {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError(t("auth.passwordMin"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    if (!agreedToTerms) {
      setError(t("auth.mustAgree"));
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, username, firstName, lastName);
      navigate("/verify");
    } catch (err) {
      setError(err.error || t("auth.registerFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
    <div className="min-h-full flex flex-col items-center justify-center px-6 py-8">
      <img
        src={theme === "dark" ? "/pinned-logo-light-stacked.png" : "/pinned-logo-dark-stacked.png"}
        alt={t("app.title")}
        className="w-64 mb-3"
      />
      <p className="text-th-muted text-sm mb-8 text-center">
        {t("auth.tagline")}
      </p>

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-3">
        {error && (
          <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">
            {error}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            placeholder={t("auth.firstName")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
          />
          <input
            type="text"
            placeholder={t("auth.lastName")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
          />
        </div>
        <input
          type="text"
          placeholder={t("auth.username")}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
        <input
          type="email"
          placeholder={t("auth.email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
        <input
          type="password"
          placeholder={t("auth.passwordHint")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
          className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
        <input
          type="password"
          placeholder={t("auth.confirmPassword")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
          className="w-full px-4 py-3.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
        <p className="text-[11px] text-th-muted leading-relaxed px-1">
          {t("auth.privacyNotice")}
        </p>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-th-border accent-th-accent flex-shrink-0"
          />
          <span className="text-xs text-th-muted leading-relaxed">
            {t("auth.agreePrefix")}{" "}
            <Link to="/terms" className="text-th-accent hover:underline">{t("auth.termsLink")}</Link>
            {" "}{t("auth.agreeAnd")}{" "}
            <Link to="/privacy" className="text-th-accent hover:underline">{t("auth.privacyLink")}</Link>.
            {" "}{t("auth.ageConfirm")}
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting || !agreedToTerms}
          className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-50 hover:opacity-90 active:scale-[0.98] transition-all"
        >
          {submitting ? t("auth.creatingAccount") : t("auth.signup")}
        </button>
      </form>

      <p className="text-sm text-th-muted mt-6">
        {t("auth.hasAccount")}{" "}
        <Link to="/login" className="text-th-accent font-medium">
          {t("auth.login")}
        </Link>
      </p>
    </div>
    </div>
  );
}

export default RegisterPage;
